import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { Col, Row, Spin } from "antd";
import Header from "pages/user-management/userDetails/Header";
import { useParams } from "react-router";
import { getUserByEmail, retrieveAllTaskRoutines, retrieveAllTasks, retrieveUserLeaves } from "utils/api";
import { defaultNgiErrorMethod } from "components/Common/responses/message";
import { makeStyles } from "@material-ui/core/styles";
import Card from "antd/es/card/Card";
import infoIconGrey from "assets/images/svg/InfoGrey.svg";
import leavesIcon from "assets/images/svg/leavesIcon.svg";
import UserCard from "pages/user-management/userDetails/UserCard";
import General from "pages/user-management/userDetails/Tabs/General";
import LeavesBar from "pages/user-management/userDetails/LeavesBar";
import Leaves from "pages/user-management/userDetails/Tabs/Leaves";
import NotFoundContainer from "components/Common/NotFoundContainer";
import { AUTH_USER, USER_ROLES } from "utils/constants";
import { someUserHasOneOfRoles } from "utils/helpers/functions";
import tasks from "assets/images/svg/tasks.svg";
import allAssets from "assets/images/svg/allAccets.svg";
import { history } from "store";
import interventionCallIcon from "assets/images/svg/intervention-calls.svg";

const useStyles = makeStyles({
  root: {
    width: "100%",
    marginTop: 30,
  },
  container: {
    marginTop: 10,
    borderRadius: 5,
    padding: 15,
  },
  row: {
    marginTop: 20,
  },
  breadcrumbs: {
    textTransform: "capitalize",
    "&> a": {
      textDecoration: "underline !important",
      color: "black",
    },
  },
});

const UserDetails = () => {
  const { email } = useParams();
  const [user, setUser] = useState({});
  const [route, setRoute] = useState("");
  const [leaves, setLeaves] = useState([]);
  const [isLoading, setLoader] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [userRoutine, setUserRoutine] = useState(null);
  const [hasTaskInProgress, setHasTaskInProgress] = useState(false);
  const classes = useStyles();

  const getUser = useCallback(() => {
    getUserByEmail(email)
      .then(res => {
        setUser(res?.data?.result);
      })
      .catch(error => {
        setLoader(false);
        if (error.response?.data?.errors[0]?.code === "NOT_FOUND") {
          setIsNotFound(true);
        }
      });
  }, [email]);

  const getUserLeaves = useCallback(() => {
    retrieveUserLeaves(email)
      .then(res => {
        setLeaves(res?.data?.result?.items);
      })
      .catch(err => {
        defaultNgiErrorMethod(err);
      });
  }, [email]);

  const getRoutine = useCallback(() => {
    retrieveAllTaskRoutines({ assigneeId: email }).then(res => {
      const {
        result: { items },
      } = res?.data;
      const foundRoutine = items.find(item => item.status === "Open" || item.status === "Suspended");
      setUserRoutine(foundRoutine || items[0]);
    });
  }, [email]);

  const onCardClick = useCallback(e => {
    const route = e.currentTarget.dataset.route;
    const link = e.currentTarget.dataset.link;
    if (route) {
      setRoute(route);
      return;
    }
    history.push(link);
  }, []);

  const onCardClickLink = useCallback(
    e => {
      let route = "GENERAL_INFORMATION";
      if (userRoutine.status !== "Close") route = e.currentTarget.dataset.route;
      history.push(`/operations/routine/${userRoutine.id}?activeTab=${route}&backUrl=/user/${email}`);
    },
    [userRoutine, email]
  );

  const handleBack = useCallback(e => {
    e.preventDefault();
    setRoute("");
  }, []);

  const loadData = useCallback(() => {
    retrieveAllTasks({
      category: "Intervention Call",
      depot: AUTH_USER()?.depotName,
      pageSize: 1000,
    }).then(res => {
      const {
        result: { items },
      } = res?.data;
      const foundInProgress = items?.find(item => item.status === "In progress");

      setHasTaskInProgress(!!foundInProgress);
    });
  }, []);

  useEffect(() => {
    onUpdateData();
    loadData();
  }, []);

  const onUpdateData = useCallback(() => {
    getUser();
    getUserLeaves();
    getRoutine();
  }, [getUser, getUserLeaves]);
  const hasRole = useCallback(
    () =>
      [
        USER_ROLES.TECHNICIANS,
        USER_ROLES.MTSR,
        USER_ROLES.TSS,
        USER_ROLES.SALES,
        USER_ROLES.OPERATION_MANAGERS,
        USER_ROLES.CUSTOMER_CARE,
        USER_ROLES.CX_INTEGRATION,
        USER_ROLES.CUSTOMER_CARE_LEADERSHIP,
        USER_ROLES.IT_OPERATIONS,
        USER_ROLES.IT_OPERATIONS_LEADERSHIP,
        USER_ROLES.SYSTEM_FULL_ADMINS,
      ].some(role => user?.roles?.includes(role)),
    [user]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <NotFoundContainer isNotFound={isNotFound}>
          <Spin spinning={isLoading}>
            <>
              <Header user={user} updateUserData={onUpdateData} />
              <LeavesBar leaves={leaves} updateUser={onUpdateData} />
              {!route && (
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} align={"stretch"} className={classes.row}>
                  <Col xs={24} sm={12} md={8} lg={8} xl={6} xxl={4}>
                    <UserCard
                      route={"general"}
                      icon={infoIconGrey}
                      title={"General Information"}
                      subtitle={"View employee general information and more"}
                      onCardClick={onCardClick}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={8} xl={6} xxl={4}>
                    <UserCard
                      route={"leaves"}
                      icon={leavesIcon}
                      title={"Leave"}
                      subtitle={"History of vacations and other types of leave taken"}
                      onCardClick={onCardClick}
                    />
                  </Col>
                  {hasRole() && (
                    <Col xs={24} sm={12} md={8} lg={8} xl={6} xxl={4}>
                      <UserCard
                        link={`/operations/tasks/intervention-call?backUrl=/user/${email}`}
                        icon={interventionCallIcon}
                        title="Intervention Calls"
                        subtitle="Assign, execute and complete intervention calls"
                        onCardClick={onCardClick}
                        colorBadge={hasTaskInProgress ? "#f18b39" : ""}
                      />
                    </Col>
                  )}

                  {someUserHasOneOfRoles(
                    [USER_ROLES.TRAINING, USER_ROLES.MTSR, USER_ROLES.TSS, USER_ROLES.TECHNICIANS, USER_ROLES.MMT],
                    user
                  ) &&
                    userRoutine && (
                      <Col xs={24} sm={12} md={8} lg={8} xl={6} xxl={4}>
                        <UserCard
                          route="PRIORITY_TASKS"
                          icon={tasks}
                          title="Tasks"
                          subtitle="View task list from user routine"
                          onCardClick={onCardClickLink}
                        />
                      </Col>
                    )}
                  {someUserHasOneOfRoles(
                    [
                      USER_ROLES.TRAINING,
                      USER_ROLES.MTSR,
                      USER_ROLES.TSS,
                      USER_ROLES.TECHNICIANS,
                      USER_ROLES.MMT,
                      USER_ROLES.LOGISTICS,
                    ],
                    user
                  ) &&
                    userRoutine && (
                      <Col xs={24} sm={12} md={8} lg={8} xl={6} xxl={4}>
                        <UserCard
                          route="ASSETS"
                          icon={allAssets}
                          title="Assets"
                          subtitle="View assigned assets to the user"
                          onCardClick={onCardClickLink}
                        />
                      </Col>
                    )}
                </Row>
              )}
              {!!route && (
                <div className={classes.root}>
                  <span className={classes.breadcrumbs}>
                    <a href="#" onClick={handleBack}>{`Back`}</a>
                    {` > ${route}`}
                  </span>
                  <Card className={classes.container}>
                    {route === "general" && <General user={user} />}
                    {route === "leaves" && <Leaves leaves={leaves} setLoader={setLoader} updateUser={onUpdateData} />}
                  </Card>
                </div>
              )}
            </>
          </Spin>
        </NotFoundContainer>
      </Container>
    </div>
  );
};

export default UserDetails;
